```vue
<template>
  <b-modal
    id="item-edit-popup"
    ref="modalRef"
    :title="$t('e_invoice_detail_edit_item') + ' - ' + invoiceTypeDisplay"
    @ok="saveChanges"
    @hidden="resetForm"
    @hide="hideModal"
    centered
    size="xl"
    no-close-on-backdrop
  >

    <!-- Layout dạng thẻ nằm ngang giống bảng -->
    <div class="item-edit-container">
      <div class="item-edit-header d-flex align-items-center">
        <!-- Các tiêu đề cột -->
        <div class="edit-column">{{ $t('product_service_name') }}</div>
        <div class="edit-column">{{ $t('common_unit') }}</div>
        <div class="edit-column text-right">{{ $t('common_quantity_abbreviation') }}</div>
        <div class="edit-column text-right">{{ $t('common_unit_price') }}</div>
        <div class="edit-column text-right" v-if="showDiscountColumnCloned">{{ $t('discount_amount') }}</div>
        <div class="edit-column text-right" v-if="showChargePercentColumnCloned">{{ $t('common_charge') }}</div>
        <div class="edit-column text-right" v-if="showChargeColumnCloned">{{ $t('common_charge_amount') }}</div>
        <div class="edit-column text-right" v-if="showTaxPercentColumnCloned">{{ $t('common_tax') }}</div>
        <div class="edit-column text-right" v-if="showTaxColumnCloned">{{ $t('common_tax_amount') }}</div>
        <div class="edit-column text-right">{{ $t('common_amount') }}</div>
        <div class="edit-column text-right" v-if="isClonedBHInvoice">{{ $t('tax_reduction') }}</div>
      </div>

      <div class="item-edit-body d-flex align-items-center mb-3">
        <!-- Các ô input và kết quả tính toán -->
        <div class="edit-column">
          <b-form-input
            v-model="editItem.ItemName"
            required
            maxlength="100"
            pattern="^[a-zA-Z0-9\u00C0-\u1EF9\s\W]*$"
            title="Cho phép nhập a-z, A-Z, 0-9 và ký tự đặc biệt"
          ></b-form-input>
        </div>
        <div class="edit-column">
          <b-form-input
            v-model="editItem.UnitName"
            maxlength="100"
            pattern="^[a-zA-Z0-9\u00C0-\u1EF9\s\W]*$"
            title="Cho phép nhập a-z, A-Z, 0-9 và ký tự đặc biệt"
          ></b-form-input>
        </div>
        <div class="edit-column">
          <vue-cleave
            v-model="editItem.Quantity"
            :options="dynamicCleaveOptions.quantity"
            :raw="true"
            class="form-control text-right"
            data-field="Quantity"
            @input="value => handleCleaveInput(value, 'Quantity')"
          ></vue-cleave>
        </div>
        <div class="edit-column">
          <vue-cleave
            v-model="editItem.Price"
            :options="dynamicCleaveOptions.price"
            :raw="true"
            class="form-control text-right"
            data-field="Price"
            @input="value => handleCleaveInput(value, 'Price')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="showDiscountColumnCloned">
          <vue-cleave
            v-model="editItem.DiscountAmount"
            :options="dynamicCleaveOptions.currency"
            :raw="true"
            class="form-control text-right"
            data-field="DiscountAmount"
            @input="value => handleCleaveInput(value, 'DiscountAmount')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="showChargePercentColumnCloned">
          <vue-cleave
            v-model="editItem.Charges"
            :options="dynamicCleaveOptions.percentage"
            :raw="true"
            class="form-control text-right"
            data-field="Charges"
            @input="value => handleCleaveInput(value, 'Charges')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="showChargeColumnCloned">
          <b-form-input
            v-if="isAutoCalculateMoney"
            :value="formatMoneyWithNoFractionHandler(calculatedValues.chargeAmount)"
            disabled
            class="text-right calculated-field"
          ></b-form-input>
          <vue-cleave
            v-else
            v-model="editItem.ChargesAmount"
            :options="dynamicCleaveOptions.currency"
            :raw="true"
            class="form-control text-right manual-input"
            data-field="ChargesAmount"
            @input="value => handleCleaveInput(value, 'ChargesAmount')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="showTaxPercentColumnCloned">
          <vue-cleave
            v-model="editItem.Tax"
            :options="dynamicCleaveOptions.percentage"
            :raw="true"
            class="form-control text-right"
            data-field="Tax"
            @input="value => handleCleaveInput(value, 'Tax')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="showTaxColumnCloned">
          <b-form-input
            v-if="isAutoCalculateMoney"
            :value="formatMoneyWithNoFractionHandler(calculatedValues.taxAmount)"
            disabled
            class="text-right calculated-field"
          ></b-form-input>
          <vue-cleave
            v-else
            v-model="editItem.TaxAmount"
            :options="dynamicCleaveOptions.currency"
            :raw="true"
            class="form-control text-right manual-input"
            data-field="TaxAmount"
            @input="value => handleCleaveInput(value, 'TaxAmount')"
          ></vue-cleave>
        </div>
        <div class="edit-column">
          <vue-cleave
            v-model="editItem.TotalAmount"
            :options="dynamicCleaveOptions.currency"
            :raw="true"
            class="form-control text-right"
            :class="{'editable-total': isAutoCalculateMoney}"
            data-field="TotalAmount"
            @input="value => isAutoCalculateMoney ? handleTotalAmountCleaveInput(value, 'TOTAL_AMOUNT_EDIT') : handleCleaveInput(value, 'TotalAmount')"
          ></vue-cleave>
        </div>
        <div class="edit-column" v-if="isClonedBHInvoice">
          <b-form-input
            v-if="isAutoCalculateMoney"
            :value="formatMoneyWithNoFractionHandler(calculatedValues.taxReductionAmount)"
            disabled
            class="text-right calculated-field"
          ></b-form-input>
          <vue-cleave
            v-else
            v-model="editItem.TaxReduction43Amount"
            :options="dynamicCleaveOptions.currency"
            :raw="true"
            class="form-control text-right manual-input"
            data-field="TaxReduction43Amount"
            @input="value => handleCleaveInput(value, 'TaxReduction43Amount')"
          ></vue-cleave>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$bvModal.hide('item-edit-popup')"
      >
        {{ $t('common_cancel') }}
      </b-button>
      <b-button
        variant="primary"
        @click="saveChanges"
      >
        {{ $t('common_save') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapState } from 'vuex'
import VueCleave from 'vue-cleave-component'

export default {
  components: {
    ToastificationContent,
    VueCleave
  },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    eInvoiceConfig: {
      type: Object,
      required: true,
      default: () => ({})
    },
    isBHInvoice: {
      type: Boolean,
      required: false,
      default: false
    },
    showDiscountColumn: {
      type: Function,
      required: true
    },
    showChargeColumn: {
      type: Function,
      required: true
    },
    showChargePercentColumn: {
      type: Function,
      required: true
    },
    showTaxColumn: {
      type: Function,
      required: true
    },
    showTaxPercentColumn: {
      type: Function,
      required: true
    },
    localFormatMoneyWithNoFraction: {
      type: Function,
      required: false,
      default: null
    },
    calculateInvertedUnitPrice: {
      type: Function,
      required: true
    },
    calculateInvertedChargeAmount: {
      type: Function,
      required: true
    },
    calculateInvertedTaxAmount: {
      type: Function,
      required: true
    },
    calculateTaxReduction: {
      type: Function,
      required: true
    },
    isAutoCalculateMoney: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      isUpdatingFromTotalEdit: false,
      isAutoUpdatingTotal: false,
      lastEditedField: '',
      editItem: {},
      originalItem: {},
      originalConfig: {},
      isModalOpen: false,
      isEditingTotal: false, // Flag để theo dõi khi đang chỉnh sửa tổng tiền
      defaultConfigs: {
        VAT_INVOICE: {
          InvoiceType: "VAT_INVOICE",
          ChargeOption: "SEPARATE_COLUMN",
          DiscountOption: "SEPARATE_COLUMN",
          ShowChargePercent: true,
          ShowRepresentativeName: true,
          ShowTaxDetail: true,
          ShowTaxPercent: true,
          TaxOption: "SEPARATE_COLUMN"
        },
        BH_INVOICE: {
          InvoiceType: "BH_INVOICE",
          ChargeOption: "SEPARATE_COLUMN",
          DiscountOption: "SEPARATE_COLUMN",
          ShowChargePercent: true,
          ShowRepresentativeName: true,
          ShowTaxDetail: true,
          ShowTaxPercent: true,
          TaxOption: "SEPARATE_COLUMN"
        },
        SALES_INVOICE: {
          InvoiceType: "SALES_INVOICE",
          ChargeOption: "SEPARATE_COLUMN",
          DiscountOption: "SEPARATE_COLUMN",
          ShowChargePercent: true,
          ShowRepresentativeName: true,
          ShowTaxDetail: false,
          ShowTaxPercent: false,
          TaxOption: "SEPARATE_COLUMN"
        }
      },
      clonedConfig: {},
      // Cleave options for currency formatting
      cleaveOptions: {
        currency: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: 0,
          delimiter: '.',
          numeralDecimalMark: ','
        },
        price: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: 2,
          delimiter: '.',
          numeralDecimalMark: ','
        },
        percentage: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 2,
          numeralDecimalMark: ','
        },
        quantity: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 3,
          numeralDecimalMark: ','
        }
      }
    };
  },
  computed: {
    ...mapState({
      $store_eInvoiceConfig: (state) => state.eInvoiceManagement.eInvoiceConfig,
    }),
    calculatedValues() {
      // Nếu auto calculate bị tắt, sử dụng giá trị trực tiếp từ editItem
      if (!this.isAutoCalculateMoney ) {
        return {
          chargeAmount: +this.editItem.ChargesAmount || 0,
          taxAmount: +this.editItem.TaxAmount || 0,
          totalAmount: +this.editItem.TotalAmount || 0,
          taxReductionAmount: +this.editItem.TaxReduction43Amount || 0
        };
      }
      
      // Nếu đang chỉnh sửa tổng tiền, sử dụng giá trị đã tính ngược
      if (this.isEditingTotal) {
        return {
          chargeAmount: this.editItem.ChargesAmount || 0,
          taxAmount: this.editItem.TaxAmount || 0,
          totalAmount: this.editItem.TotalAmount || 0,
          taxReductionAmount: this.isClonedBHInvoice ? this.calculateTaxReduction(this.editItem) : 0
        };
      }
      
      // Create a temporary object for calculation
      const tempItem = { ...this.editItem };
      tempItem.DisplayUnitPrice = +tempItem.Price;
      tempItem.InvoiceType = this.clonedConfig.InvoiceType;
      
      // Calculate values based on what was edited
      const chargeAmount = this.calculateChargeAmountInternal(tempItem);
      const taxAmount = this.calculateTaxAmountInternal(tempItem, chargeAmount);
      const totalAmount = this.calculateTotalAmountInternal(tempItem, chargeAmount, taxAmount);
      
      // Update editItem with calculated values
      this.editItem.ChargesAmount = chargeAmount;
      this.editItem.TaxAmount = taxAmount;
      this.editItem.TotalAmount = totalAmount;
      
      // Calculate tax reduction amount for BH invoices
      const taxReductionAmount = this.isClonedBHInvoice ? 
        this.calculateTaxReductionInternal(tempItem, taxAmount) : 0;
      
      if (this.isClonedBHInvoice) {
        this.editItem.TaxReduction43Amount = taxReductionAmount;
      }
      
      return {
        chargeAmount,
        taxAmount,
        totalAmount,
        taxReductionAmount
      };
    },
    
    // Kiểm tra hóa đơn BH dựa trên cấu hình đã clone
    isClonedBHInvoice() {
      return this.clonedConfig.InvoiceType === 'BH_INVOICE';
    },
    
    // Hiển thị cột dựa trên cấu hình đã clone
    showDiscountColumnCloned() {
      return this.clonedConfig.DiscountOption === 'SEPARATE_COLUMN';
    },
    
    showChargeColumnCloned() {
      return this.clonedConfig.ChargeOption === 'SEPARATE_COLUMN' || this.clonedConfig.ChargeOption === 'DISPLAY_IN_TOTAL';
    },
    
    showChargePercentColumnCloned() {
      return this.clonedConfig.ShowChargePercent === true;
    },
    
    showTaxColumnCloned() {
      return this.clonedConfig.TaxOption === 'SEPARATE_COLUMN' && this.clonedConfig.InvoiceType !== 'SALES_INVOICE';
    },
    
    showTaxPercentColumnCloned() {
      return this.clonedConfig.ShowTaxPercent === true && this.clonedConfig.InvoiceType !== 'SALES_INVOICE';
    },
    
    // Thêm các computed properties để làm rõ loại hóa đơn
    invoiceTypeDisplay() {
      const invoiceType = this.clonedConfig?.InvoiceType || 'VAT_INVOICE';
      switch (invoiceType) {
        case 'VAT_INVOICE':
          return 'Hóa đơn GTGT';
        case 'BH_INVOICE':
          return 'Hóa đơn bán hàng';
        case 'SALES_INVOICE':
          return 'Hóa đơn bán hàng (không thuế)';
        default:
          return 'Hóa đơn';
      }
    },
    
    // Computed property to safely use the money formatter
    formatMoneyWithNoFractionHandler() {
      // If prop is provided, use it, otherwise use local implementation
      return this.localFormatMoneyWithNoFraction || this.formatMoneyWithNoFractionLocal;
    },
    
    // Dynamic cleave options based on invoice configuration
    dynamicCleaveOptions() {
      const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      console.log('Dynamic Cleave Options:', { priceMinorUnit, minorUnit, config: this.eInvoiceConfig });
      
      return {
        currency: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: minorUnit,
          numeralDecimalMark: '.',
          delimiter: ',',
          stripLeadingZeroes: false
        },
        price: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: priceMinorUnit,
          numeralDecimalMark: '.',
          delimiter: ',',
          stripLeadingZeroes: false
        },
        percentage: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 0,
          numeralDecimalMark: '.',
          delimiter: ',',
          stripLeadingZeroes: false
        },
        quantity: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 2,
          numeralDecimalMark: '.',
          delimiter: ',',
          stripLeadingZeroes: false
        }
      };
    }
  },
  methods: {
    calculateChargeAmountInternal(item) {
      const precedingFields = ['Quantity', 'Price', 'DiscountAmount', 'Charges'];
    
    if (!precedingFields.includes(this.lastEditedField) && this.lastEditedField !== '') {
      // Return current value if not editing a preceding field
      return +this.editItem.ChargesAmount || 0;
    }
      if (!item || typeof item !== 'object') return 0;
      
      const quantity = +item.Quantity || 0;
      const price = +item.Price || 0;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Round the price according to MinorUnit if MinorUnit is 0
      const effectivePrice = minorUnit === 0 ? Math.round(price) : price;
      
      const discountAmount = +item.DiscountAmount || 0;
      const chargePercent = +item.Charges || 0;
      
      const invoiceType = this.clonedConfig.InvoiceType;
      const discountOption = this.clonedConfig.DiscountOption;
      const chargeOption = this.clonedConfig.ChargeOption;
      
      let baseAmount = 0;
      let chargeAmount = 0;
      
      // Tính baseAmount dựa trên cấu hình
      if (discountOption === 'SEPARATE_COLUMN') {
        // Giảm giá hiển thị riêng
        baseAmount = quantity * effectivePrice - discountAmount;
      } else {
        // Giảm giá gộp vào đơn giá
        baseAmount = quantity * effectivePrice;
      }
      
      // Tính chargeAmount dựa trên cấu hình
      if (chargeOption === 'SEPARATE_COLUMN' || chargeOption === 'DISPLAY_IN_TOTAL' || chargeOption === 'SEPARATE_ROW') {
        // Phí hiển thị riêng, hiển thị ở tổng, hoặc tách thành dòng riêng
        chargeAmount = baseAmount * chargePercent / 100;
      } else {
        // Phí gộp vào đơn giá
        chargeAmount = 0;
      }
      
      // Làm tròn đến minorUnit
      return this.roundTo(chargeAmount, minorUnit);
    },
    
    calculateTaxAmountInternal(item, chargeAmount) {
      const precedingFields = ['Quantity', 'Price', 'DiscountAmount', 'Charges', 'ChargesAmount', 'Tax'];
    
      if (!precedingFields.includes(this.lastEditedField) && this.lastEditedField !== '') {
        // Return current value if not editing a preceding field
        return +this.editItem.TaxAmount || 0;
      }
      if (!item || typeof item !== 'object') return 0;
      
      const quantity = +item.Quantity || 0;
      const price = +item.Price || 0;

      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Round the price according to MinorUnit if MinorUnit is 0
      const effectivePrice = minorUnit === 0 ? Math.round(price) : price;
      
      const discountAmount = +item.DiscountAmount || 0;
      const taxPercent = +item.Tax || 0;
      
      const invoiceType = this.clonedConfig.InvoiceType;
      const discountOption = this.clonedConfig.DiscountOption;
      const chargeOption = this.clonedConfig.ChargeOption;
      
      // Nếu là SALES_INVOICE, không tính thuế
      if (invoiceType === 'SALES_INVOICE') return 0;
      
      let baseAmount = 0;
      
      // Tính baseAmount (số tiền chịu thuế) dựa trên cấu hình
      if (discountOption === 'SEPARATE_COLUMN') {
        // Giảm giá hiển thị riêng
        baseAmount = quantity * effectivePrice - discountAmount;
      } else {
        // Giảm giá gộp vào đơn giá
        baseAmount = quantity * effectivePrice;
      }
      
      // Thêm phí vào baseAmount nếu phí được tính vào trước thuế
      if (chargeOption === 'SEPARATE_COLUMN') {
        baseAmount += chargeAmount;
      }
      // Với SEPARATE_ROW, phí sẽ được hiển thị riêng nên không cộng vào baseAmount ở đây
      
      // Tính thuế
      const taxAmount = baseAmount * taxPercent / 100;
      
      // Làm tròn đến minorUnit
      return this.roundTo(taxAmount, minorUnit);
    },
    
    calculateTotalAmountInternal(item, chargeAmount, taxAmount) {
      if (!item || typeof item !== 'object') return 0;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      const quantity = +item.Quantity || 0;
      const price = +item.Price || 0;
      const discountAmount = +item.DiscountAmount || 0;
      const effectivePrice = minorUnit === 0 ? Math.round(price) : price;
      const invoiceType = this.clonedConfig.InvoiceType;
      const discountOption = this.clonedConfig.DiscountOption;
      const chargeOption = this.clonedConfig.ChargeOption;
      
      
      let baseAmount = 0;
      
      // Tính baseAmount dựa trên cấu hình
      if (discountOption === 'SEPARATE_COLUMN') {
        // Giảm giá hiển thị riêng
        baseAmount = quantity * effectivePrice - discountAmount;
      } else {
        // Giảm giá gộp vào đơn giá
        baseAmount = quantity * effectivePrice;
      }
      
      // Cộng phí vào totalAmount nếu phí được hiển thị riêng
      if (chargeOption === 'SEPARATE_COLUMN') {
        baseAmount += chargeAmount;
      }
      // Với SEPARATE_ROW, phí sẽ được hiển thị ở dòng riêng nên không cộng vào baseAmount ở đây
      
      let totalAmount = baseAmount;
      
      // Với BH_INVOICE, KHÔNG cộng thuế vào totalAmount, chỉ trừ giảm thuế
      if (invoiceType === 'BH_INVOICE') {
        const taxReduction = this.calculateTaxReductionInternal(item, taxAmount);
        totalAmount = baseAmount - taxReduction;
      } else {
        // Với VAT_INVOICE và các loại khác, cộng thuế vào totalAmount
        totalAmount = baseAmount + taxAmount;
      }
      
      // Làm tròn đến minorUnit
      return this.roundTo(totalAmount, minorUnit);
    },
    
    calculateTaxReductionInternal(item, taxAmount) {
      if (this.clonedConfig.InvoiceType !== 'BH_INVOICE') return 0;
      
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Tính giảm trừ thuế 20% cho hóa đơn BH
      const taxReduction = taxAmount * 0.2;
      
      // Làm tròn đến minorUnit
      return this.roundTo(taxReduction, minorUnit);
    },
    
    roundTo(value, decimals) {
      const factor = Math.pow(10, decimals);
      return Math.round(value * factor) / factor;
    },
    
    formatMoney(value) {
      const decimals = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      let num = parseFloat(this.parseInputValue(value));
      if (isNaN(num)) return '0' + (decimals > 0 ? ',' + '0'.repeat(decimals) : '');
      
      // Làm tròn giá trị trước khi định dạng
      num = this.roundTo(num, decimals);

      return new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(num);
    },

    formatMoneyWithNoFractionLocal(value) {
      const decimals = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      let num = parseFloat(this.parseInputValue(value));
      if (isNaN(num)) return '0';
      
      // Làm tròn giá trị trước khi định dạng
      num = this.roundTo(num, decimals);

      return new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(num);
    },
    
    // Phương thức xử lý input từ Vue Cleave
    handleCleaveInput(value, field) {
      this.lastEditedField = field;
      // Reset flag isEditingTotal nếu chỉnh sửa field khác TotalAmount
      if (field !== 'TotalAmount' && !this.isUpdatingFromTotalEdit) {
        this.isEditingTotal = false;
      }
      
      // Với vue-cleave-component và :raw="true", value sẽ là raw number
      let numericValue = parseFloat(value) || 0;
      
      // Log để debug
      console.log(`Field: ${field}, Input Value: ${value}, Parsed: ${numericValue}`);
      
      // Xử lý đặc biệt cho Price để tránh làm tròn sớm
      if (field === 'Price') {
        // Lấy chính xác PriceMinorUnit
        const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
        console.log(`PriceMinorUnit: ${priceMinorUnit}`);
        
        // Cập nhật giá trị mà không làm tròn giá
        this.editItem[field] = numericValue;
        
        // Đánh dấu đang cập nhật tự động để tránh handleTotalAmountCleaveInput
        this.isAutoUpdatingTotal = true;
        
        // Reset flag sau khi Vue đã render xong
        this.$nextTick(() => {
          setTimeout(() => {
            this.isAutoUpdatingTotal = false;
          }, 50);
        });
      } else {
        // Các trường khác xử lý bình thường
        this.editItem[field] = numericValue;
      }
      
      // Xử lý đặc biệt cho SEPARATE_ROW + ADD_TO_UNIT_PRICE
      if ((field === 'Price' || field === 'DiscountAmount' || field === 'Quantity') && 
          this.clonedConfig.ChargeOption === 'SEPARATE_ROW' && 
          this.clonedConfig.DiscountOption === 'ADD_TO_UNIT_PRICE') {
        
        const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
        const discountPerUnit = (+this.editItem.DiscountAmount || 0) / Math.max(+this.editItem.Quantity || 1, 1);
        this.editItem.DisplayUnitPrice = this.roundTo(+this.editItem.Price - discountPerUnit, priceMinorUnit);
      }
    },
    
    // Phương thức xử lý khi người dùng chỉnh sửa tổng tiền với Vue Cleave
    handleTotalAmountCleaveInput(value, source = '') {
      if (source !== 'TOTAL_AMOUNT_EDIT') {
        return;
      }
      
      this.lastEditedField = 'TotalAmount';

      if (this.isAutoUpdatingTotal) {
        console.log('Bỏ qua tính ngược do TotalAmount đang được cập nhật tự động');
        return;
      }
      
      // if(!this.isEditingTotal) {
      //   console.log('Đang trong quá trình chỉnh sửa tổng tiền, bỏ qua tính ngược thêm lần nữa');
      //   return;

      // }
      // Đánh dấu đang chỉnh sửa tổng tiền
      this.isEditingTotal = true;
      
      // Parse giá trị từ input
      let numericValue = parseFloat(value) || 0;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      numericValue = this.roundTo(numericValue, minorUnit);
      
      // Log để debug
      console.log(`Total Amount - Input Value: ${value}, Parsed: ${numericValue}`);
      
      // Cập nhật giá trị tổng tiền
      this.editItem.TotalAmount = numericValue;
      
      // Tạo bản sao của item để tính toán
      const tempItem = { ...this.editItem };
      
      // Tính ngược từ tổng tiền
      const invertedPrice = this.calculateInvertedUnitPrice(tempItem);
      const invertedChargeAmount = this.calculateInvertedChargeAmount(tempItem);
      const invertedTaxAmount = this.calculateInvertedTaxAmount(tempItem);
      
      // Cập nhật các giá trị tính ngược với làm tròn phù hợp
      const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      
      if (invertedPrice >= 0) {
        this.editItem.Price = this.roundTo(invertedPrice, priceMinorUnit);
        this.editItem.DisplayUnitPrice = this.roundTo(invertedPrice, priceMinorUnit);
      }
      
      if (invertedChargeAmount >= 0) {
        this.editItem.ChargesAmount = this.roundTo(invertedChargeAmount, minorUnit);
      }
      
      if (invertedTaxAmount >= 0) {
        this.editItem.TaxAmount = this.roundTo(invertedTaxAmount, minorUnit);
      }
      
      // Tính giảm thuế cho hóa đơn BH nếu cần
      if (this.isClonedBHInvoice) {
        const taxReduction = invertedTaxAmount > 0 ? 
          this.calculateTaxReduction({
            ...tempItem,
            TaxAmount: this.roundTo(invertedTaxAmount, minorUnit)
          }) : 0;
        
        if (taxReduction >= 0) {
          this.editItem.TaxReduction43Amount = this.roundTo(taxReduction, minorUnit);
        }
      }
      this.isUpdatingFromTotalEdit = true;
  
      if (invertedPrice >= 0) {
        this.editItem.Price = this.roundTo(invertedPrice, priceMinorUnit);
        this.editItem.DisplayUnitPrice = this.roundTo(invertedPrice, priceMinorUnit);
      }
      
      // Reset flag after Vue has processed the changes
      this.$nextTick(() => {
        setTimeout(() => {
          this.isUpdatingFromTotalEdit = false;
        }, 50);
      });
    },
    
    // Phương thức parse giá trị từ Cleave (loại bỏ định dạng)
    parseCleaveValue(value) {
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value !== 'string' || value === '') {
        return 0;
      }
      
      console.log('parseCleaveValue input:', value);
      
      // Với :raw="true", value từ vue-cleave-component thường là formatted string
      // nhưng có thể chứa dấu thập phân dạng chấm (.) thay vì phẩy (,)
      
      // Nếu value chứa dấu chấm cuối cùng là thập phân (không phải phân cách nghìn)
      const lastDotIndex = value.lastIndexOf('.');
      const lastCommaIndex = value.lastIndexOf(',');
      
      let cleanValue = value;
      
      // Nếu có cả dấu chấm và dấu phẩy
      if (lastDotIndex !== -1 && lastCommaIndex !== -1) {
        if (lastDotIndex > lastCommaIndex) {
          // Dấu chấm cuối là thập phân, dấu phẩy là phân cách nghìn
          cleanValue = value.replace(/,/g, '').replace(/\./g, '.');
        } else {
          // Dấu phẩy cuối là thập phân, dấu chấm là phân cách nghìn  
          cleanValue = value.replace(/\./g, '').replace(',', '.');
        }
      } else if (lastDotIndex !== -1) {
        // Chỉ có dấu chấm - cần xác định là phân cách nghìn hay thập phân
        const afterDot = value.substring(lastDotIndex + 1);
        if (afterDot.length <= 3 && /^\d+$/.test(afterDot)) {
          // Nếu sau dấu chấm có <= 3 chữ số thì có thể là thập phân
          const beforeDot = value.substring(0, lastDotIndex);
          if (beforeDot.length <= 3) {
            // Nếu trước dấu chấm có <= 3 chữ số thì chắc chắn là thập phân
            cleanValue = value; // Giữ nguyên
          } else {
            // Có thể là phân cách nghìn, nhưng cần kiểm tra thêm
            cleanValue = value; // Tạm thời giữ nguyên
          }
        } else {
          // Chắc chắn là phân cách nghìn
          cleanValue = value.replace(/\./g, '');
        }
      } else if (lastCommaIndex !== -1) {
        // Chỉ có dấu phẩy - chắc chắn là thập phân
        cleanValue = value.replace(',', '.');
      }
      
      // Parse thành số
      const numericValue = parseFloat(cleanValue);
      
      console.log('parseCleaveValue output:', numericValue);
      
      // Trả về 0 nếu không parse được
      return isNaN(numericValue) ? 0 : numericValue;
    },
    
    // Phương thức xử lý khi người dùng chỉnh sửa tổng tiền (legacy support)
    handleTotalAmountInput(event) {
      // Đánh dấu đang chỉnh sửa tổng tiền
      this.isEditingTotal = true;
      
      // Kiểm tra event tồn tại
      if (!event) {
        console.error('Event không tồn tại trong handleTotalAmountInput');
        return;
      }
      
      // Format tiền tệ như bình thường
      this.handleMoneyInput(event, 'TotalAmount');
      
      // Tạo bản sao của item đang chỉnh sửa để tính toán, đảm bảo không ảnh hưởng đến dữ liệu gốc
      const tempItem = { ...this.editItem };
      
      // Tính ngược đơn giá (Price) từ tổng tiền (TotalAmount)
      const invertedPrice = this.calculateInvertedUnitPrice(tempItem);
      
      // Tính ngược phí (ChargesAmount) từ tổng tiền (TotalAmount)
      const invertedChargeAmount = this.calculateInvertedChargeAmount(tempItem);
      
      // Tính ngược thuế (TaxAmount) từ tổng tiền (TotalAmount)
      const invertedTaxAmount = this.calculateInvertedTaxAmount(tempItem);
      
      // Làm tròn các giá trị tính toán trước khi cập nhật vào editItem
      const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Cập nhật ngay vào editItem (dữ liệu của modal clone)
      // Chỉ cập nhật nếu giá trị tính ra là hợp lệ và đã được làm tròn
      if (invertedPrice >= 0) {
        this.editItem.Price = this.roundTo(invertedPrice, priceMinorUnit);
        this.editItem.DisplayUnitPrice = this.roundTo(invertedPrice, priceMinorUnit);
      }
      
      if (invertedChargeAmount >= 0) {
        this.editItem.ChargesAmount = this.roundTo(invertedChargeAmount, minorUnit);
      }
      
      if (invertedTaxAmount >= 0) {
        this.editItem.TaxAmount = this.roundTo(invertedTaxAmount, minorUnit);
      }
      
      // Nếu là hóa đơn BH, tính thêm giá trị giảm thuế (TaxReduction43Amount)
      if (this.isClonedBHInvoice) {
        const taxReduction = invertedTaxAmount > 0 ? 
          this.calculateTaxReduction({
            ...tempItem,
            TaxAmount: this.roundTo(invertedTaxAmount, minorUnit)
          }) : 0;
        
        if (taxReduction >= 0) {
          this.editItem.TaxReduction43Amount = this.roundTo(taxReduction, minorUnit);
        }
      }
      
      // Hiển thị hiệu ứng phản hồi trực quan khi thay đổi tổng tiền
      if (event.target) {
        const totalInput = event.target;
        totalInput.classList.add('calculated');
        setTimeout(() => {
          if (totalInput && totalInput.classList) {
            totalInput.classList.remove('calculated');
          }
        }, 300);
      }
    },
    
    // Phương thức xử lý format tiền tệ khi nhập
    handleMoneyInput(event, field) {
      // Reset flag isEditingTotal nếu chỉnh sửa field khác TotalAmount
      if (field !== 'TotalAmount') {
        this.isEditingTotal = false;
      }
      
      // Kiểm tra event và event.target tồn tại
      if (!event || !event.target) {
        console.error('Event hoặc event.target không tồn tại trong handleMoneyInput');
        return;
      }
      
      // Lấy giá trị hiện tại từ event
      let value = event.target.value || '';
      
      // Lưu lại vị trí con trỏ hiện tại
      const cursorPosition = event.target.selectionStart || 0;
      
      // Đếm số ký tự phân cách (dấu .) trước vị trí con trỏ để điều chỉnh sau khi format
      const dotsBeforeCursor = (value.substring(0, cursorPosition).match(/\./g) || []).length;
      
      // Tách phần nguyên và phần thập phân (nếu có)
      let parts = value.split(',');
      let integerPart = parts[0].replace(/\./g, '');
      let decimalPart = parts.length > 1 ? parts[1] : '';
      
      // Chỉ giữ lại ký tự số trong phần nguyên, đảm bảo không có số âm
      integerPart = integerPart.replace(/[^\d]/g, '');
      
      // Nếu rỗng thì set về 0
      if (!integerPart) integerPart = '0';
      
      // Chuyển thành số
      const numValue = parseInt(integerPart, 10);
      
      // Xác định số chữ số thập phân tùy theo trường
      let minorUnit = 0;
      if (field === 'Price') {
        minorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      } else if (['TotalAmount', 'ChargesAmount', 'TaxAmount', 'DiscountAmount', 'TaxReduction43Amount'].includes(field)) {
        minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      } else if (field === 'Tax' || field === 'Charges') {
        minorUnit = 2; // Thuế và phí thường có 2 chữ số thập phân
      }
      
      // Format số theo định dạng tiền tệ Việt Nam với số chữ số thập phân phù hợp
      let formattedValue = new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numValue);
      
      // Nếu có phần thập phân, thêm vào giá trị đã format
      if (decimalPart) {
        // Chỉ giữ lại ký tự số trong phần thập phân
        decimalPart = decimalPart.replace(/[^\d]/g, '');
        
        // Giới hạn độ dài phần thập phân tùy theo trường
        if (minorUnit > 0 && decimalPart.length > 0) {
          decimalPart = decimalPart.substring(0, minorUnit);
          // Xóa dấu phẩy thập phân cuối nếu đã có trong formattedValue
          if (formattedValue.includes(',')) {
            formattedValue = formattedValue.split(',')[0];
          }
          formattedValue += ',' + decimalPart;
        }
      }
      
      // Lưu giá trị số vào model (chuyển đổi dấu phẩy thành dấu chấm cho đúng định dạng số JavaScript)
      let numericValue = parseFloat((numValue + (decimalPart ? '.' + decimalPart : '')).replace(',', '.'));
      
      // Làm tròn giá trị theo minorUnit (số chữ số thập phân)
      numericValue = this.roundTo(numericValue, minorUnit);
      
      // Cập nhật giá trị đã làm tròn vào editItem
      this.editItem[field] = numericValue || 0;
      
      // Nếu thay đổi các trường ảnh hưởng đến tính toán khác ngoài TotalAmount,
      // cập nhật lại tất cả các giá trị tính toán trong editItem (modal clone)
      if (field !== 'TotalAmount' && ['Quantity', 'Price', 'DiscountAmount', 'Charges', 'Tax'].includes(field)) {
        // Xử lý đặc biệt cho trường hợp SEPARATE_ROW + ADD_TO_UNIT_PRICE khi thay đổi DiscountAmount, Quantity hoặc Price
        if ((field === 'DiscountAmount' || field === 'Quantity' || field === 'Price') && 
            this.clonedConfig.ChargeOption === 'SEPARATE_ROW' && 
            this.clonedConfig.DiscountOption === 'ADD_TO_UNIT_PRICE') {
          // Tính lại DisplayUnitPrice sau khi thay đổi giá trị liên quan
          const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
          const discountPerUnit = (+this.editItem.DiscountAmount || 0) / Math.max(+this.editItem.Quantity || 1, 1);
          this.editItem.DisplayUnitPrice = this.roundTo(+this.editItem.Price - discountPerUnit, priceMinorUnit);
        }
        
        // Tính toán lại sẽ được thực hiện tự động thông qua computed property calculatedValues
        // Do khi thay đổi editItem, calculatedValues sẽ được tính lại
      }
      
      // Cập nhật lại hiển thị trong input
      if (event.target) {
        // Format lại với giá trị đã làm tròn
        if (field === 'Price') {
          formattedValue = this.formatMoney(numericValue);
        } else if (['TotalAmount', 'ChargesAmount', 'TaxAmount', 'DiscountAmount', 'TaxReduction43Amount'].includes(field)) {
          formattedValue = this.formatMoneyWithNoFractionLocal(numericValue);
        } else {
          formattedValue = new Intl.NumberFormat('vi-VN', {
            minimumFractionDigits: minorUnit,
            maximumFractionDigits: minorUnit
          }).format(numericValue);
        }
        
        event.target.value = formattedValue;
        
        // Tính toán vị trí con trỏ mới sau khi format
        const newDotsCount = (formattedValue.substring(0, formattedValue.length).match(/\./g) || []).length;
        const dotDifference = newDotsCount - dotsBeforeCursor;
        
        // Điều chỉnh vị trí con trỏ
        this.$nextTick(() => {
          if (event.target && typeof event.target.setSelectionRange === 'function') {
            const newPosition = cursorPosition + dotDifference;
            event.target.setSelectionRange(newPosition, newPosition);
          }
        });
      }
    },

    parseInputValue(value) {
      if (typeof value === 'number') {
        return value.toString();
      }
      if (typeof value !== 'string') {
        return '';
      }
      // Loại bỏ dấu phân cách nghìn (dấu chấm ở vi-VN), giữ dấu thập phân (dấu phẩy)
      // Nhưng để linh hoạt, thay dấu phẩy thành dấu chấm để parseFloat hiểu
      return value.replace(/\./g, '').replace(/,/g, '.');
    },
    showModal(item) {
      // Reset flag chỉnh sửa tổng tiền
      this.isEditingTotal = false;
      
      // Lưu trữ tham chiếu đến item gốc
      this.originalItem = item;
      
      // Ưu tiên lấy cấu hình từ store nếu có
      const storeConfig = this.$store_eInvoiceConfig;
      this.originalConfig = storeConfig && Object.keys(storeConfig).length > 0 
        ? { ...storeConfig } 
        : { ...this.eInvoiceConfig };
      
      // Clone item để tránh thay đổi trực tiếp
      this.editItem = JSON.parse(JSON.stringify(item));
      
      // Nếu là dòng phí riêng (IsChargeRow), không cho phép chỉnh sửa
      if (item.IsChargeRow) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('information'),
            text: 'Dòng phí riêng không thể chỉnh sửa trực tiếp.',
            variant: 'info',
          },
        });
        return;
      }
      
      // Tạo cấu hình clone dựa trên loại hóa đơn
      let invoiceType = this.originalConfig.InvoiceType || item.InvoiceType || 'VAT_INVOICE';
      
      // Kiểm tra xem invoiceType có trong defaultConfigs không, nếu không thì dùng VAT_INVOICE
      if (!this.defaultConfigs[invoiceType]) {
        invoiceType = 'VAT_INVOICE';
      }
      
      // Clone cấu hình từ defaultConfigs
      this.clonedConfig = { 
        ...this.originalConfig, // Giữ lại các cài đặt khác như MinorUnit
        ...this.defaultConfigs[invoiceType] // Áp dụng cấu hình mặc định theo loại hóa đơn
      };
      
      // Xác định các minorUnit
      const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Làm tròn giá trị từ item
      this.editItem.Quantity = +this.editItem.Quantity || 0;
      this.editItem.Price = this.roundTo(+this.editItem.Price || 0, priceMinorUnit);
      this.editItem.DisplayUnitPrice = this.roundTo(+this.editItem.Price || 0, priceMinorUnit);
      this.editItem.DiscountAmount = this.roundTo(+this.editItem.DiscountAmount || 0, minorUnit);
      this.editItem.Charges = this.roundTo(+this.editItem.Charges || 0, 2);
      this.editItem.ChargesAmount = this.roundTo(+this.editItem.ChargesAmount || 0, minorUnit);
      this.editItem.Tax = this.roundTo(+this.editItem.Tax || 0, 2);
      this.editItem.TaxAmount = this.roundTo(+this.editItem.TaxAmount || 0, minorUnit);
      this.editItem.TotalAmount = this.roundTo(+this.editItem.TotalAmount || 0, minorUnit);
      
      // Xử lý đặc biệt cho trường hợp SEPARATE_ROW + ADD_TO_UNIT_PRICE
      if (this.clonedConfig.ChargeOption === 'SEPARATE_ROW' && this.clonedConfig.DiscountOption === 'ADD_TO_UNIT_PRICE') {
        // Tính discount per unit
        const discountPerUnit = (+this.editItem.DiscountAmount || 0) / Math.max(+this.editItem.Quantity || 1, 1);
        // Cập nhật DisplayUnitPrice để đã trừ discount
        this.editItem.DisplayUnitPrice = this.roundTo(+this.editItem.Price - discountPerUnit, priceMinorUnit);
      }
      
      if (this.isClonedBHInvoice) {
        this.editItem.TaxReduction43Amount = this.roundTo(+this.editItem.TaxReduction43Amount || 0, minorUnit);
      }
      
      // Xử lý đặc biệt cho SEPARATE_ROW - khôi phục giá trị phí gốc nếu đã được tách
      if (this.originalConfig.ChargeOption === 'SEPARATE_ROW' && item.ProcessedBySeparateRow) {
        // Nếu item có lưu OriginalChargesAmount (giá trị phí gốc trước khi tách)
        if (item.OriginalChargesAmount !== undefined) {
          this.editItem.ChargesAmount = this.roundTo(item.OriginalChargesAmount, minorUnit);
        } else {
          // Khôi phục ChargesAmount từ Charges (%)
          const baseAmount = (+item.Quantity || 1) * (+item.Price || 0) - (+item.DiscountAmount || 0);
          const originalChargeAmount = baseAmount * (+item.Charges || 0) / 100;
          this.editItem.ChargesAmount = this.roundTo(originalChargeAmount, minorUnit);
        }
      }
      
      // Nếu auto calculate tắt, không cần tính toán gì cả
      if (!this.isAutoCalculateMoney) {
        // Đảm bảo các trường được khởi tạo đúng và làm tròn đúng
        this.editItem.ChargesAmount = this.roundTo(+this.editItem.ChargesAmount || 0, minorUnit);
        this.editItem.TaxAmount = this.roundTo(+this.editItem.TaxAmount || 0, minorUnit);
        this.editItem.TotalAmount = this.roundTo(+this.editItem.TotalAmount || 0, minorUnit);
        
        if (this.isClonedBHInvoice) {
          this.editItem.TaxReduction43Amount = this.roundTo(+this.editItem.TaxReduction43Amount || 0, minorUnit);
        }
      } else {
        // Tính toán các giá trị dựa trên cấu hình
        const tempItem = { ...this.editItem };
        tempItem.DisplayUnitPrice = this.roundTo(+tempItem.Price, priceMinorUnit);
        tempItem.InvoiceType = this.clonedConfig.InvoiceType;
        
        // Pre-calculate values for display
        const chargeAmount = this.calculateChargeAmountInternal(tempItem);
        const taxAmount = this.calculateTaxAmountInternal(tempItem, chargeAmount);
        const totalAmount = this.calculateTotalAmountInternal(tempItem, chargeAmount, taxAmount);
        
        this.editItem.ChargesAmount = chargeAmount;
        this.editItem.TaxAmount = taxAmount;
        this.editItem.TotalAmount = totalAmount;
        
        if (this.isClonedBHInvoice) {
          const taxReduction = this.calculateTaxReductionInternal(tempItem, taxAmount);
          this.editItem.TaxReduction43Amount = taxReduction;
        }
      }
      
      // Không cần format lại giá trị cho Cleave vì nó sẽ tự động format theo options
      this.$nextTick(() => {
        // Thêm hiệu ứng highlight cho trường TotalAmount để thu hút sự chú ý
        if (this.isAutoCalculateMoney) {
          const totalAmountInput = this.$el.querySelector('[data-field="TotalAmount"]');
          if (totalAmountInput) {
            totalAmountInput.classList.add('editable-total');
          }
        }
      });
      
      this.isModalOpen = true;
      this.$nextTick(() => {
        if (this.$refs.modalRef) {
          this.$refs.modalRef.show();
        } else {
          this.$bvModal.show('item-edit-popup');
        }
      });
    },
    hideModal() {
      // Sửa lỗi phải ấn 2 lần mới tắt được popup
      if (this.isModalOpen) {
        this.isModalOpen = false;
        if (this.$refs.modalRef) {
          this.$refs.modalRef.hide();
        } else {
          this.$bvModal.hide('item-edit-popup');
        }
      }
    },
    saveChanges(bvModalEvent) {
      if (bvModalEvent) {
        bvModalEvent.preventDefault();
      }
      
      // Validate dữ liệu
      if (!this.editItem.ItemName) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('validation_error'),
            text: this.$t('product_name_required'),
            variant: 'danger',
          },
        });
        return;
      }
      
      // Xác định các minorUnit
      const priceMinorUnit = this.eInvoiceConfig?.PriceMinorUnit ?? 2;
      const minorUnit = this.eInvoiceConfig?.MinorUnit ?? 2;
      
      // Chuyển đổi các giá trị sang kiểu số và làm tròn
      this.editItem.Quantity = +this.editItem.Quantity || 0;
      this.editItem.Price = this.roundTo(+this.editItem.Price || 0, priceMinorUnit);
      this.editItem.DisplayUnitPrice = this.roundTo(+this.editItem.Price || 0, priceMinorUnit);
      this.editItem.DiscountAmount = this.roundTo(+this.editItem.DiscountAmount || 0, minorUnit);
      this.editItem.Charges = this.roundTo(+this.editItem.Charges || 0, 2); // Phần trăm phí có 2 chữ số thập phân
      this.editItem.ChargesAmount = this.roundTo(+this.editItem.ChargesAmount || 0, minorUnit);
      this.editItem.Tax = this.roundTo(+this.editItem.Tax || 0, 2); // Phần trăm thuế có 2 chữ số thập phân
      this.editItem.TaxAmount = this.roundTo(+this.editItem.TaxAmount || 0, minorUnit);
      this.editItem.TotalAmount = this.roundTo(+this.editItem.TotalAmount || 0, minorUnit);
      this.editItem.AfterTaxAmount = this.roundTo(+this.editItem.AfterTaxAmount || 0, minorUnit);
      if (this.isClonedBHInvoice) {
        this.editItem.TaxReduction43Amount = this.roundTo(+this.editItem.TaxReduction43Amount || 0, minorUnit);
      }
      
      // Giữ lại thông tin ProcessedBySeparateRow nếu có
      if (this.originalItem.ProcessedBySeparateRow) {
        this.editItem.ProcessedBySeparateRow = true;
        
        // Nếu item đã được xử lý bởi SEPARATE_ROW, lưu lại giá trị ChargesAmount ban đầu
        // để có thể khôi phục lại khi cần
        this.editItem.OriginalChargesAmount = this.roundTo(this.editItem.ChargesAmount, minorUnit);
      }
      
      // Chỉ validate khi auto calculate bật
      if (this.isAutoCalculateMoney) {
        // Nếu đang chỉnh sửa tổng tiền, giữ nguyên các giá trị đã tính ngược
        if (this.isEditingTotal) {
          // Đảm bảo rằng các giá trị đã được tính đúng bằng các hàm tính ngược
          this.editItem.TotalAmount = this.roundTo(+this.editItem.TotalAmount || 0, minorUnit);
          this.editItem.ChargesAmount = this.roundTo(+this.editItem.ChargesAmount || 0, minorUnit);
          this.editItem.TaxAmount = this.roundTo(+this.editItem.TaxAmount || 0, minorUnit);
          
          if (this.isClonedBHInvoice) {
            this.editItem.TaxReduction43Amount = this.roundTo(+this.editItem.TaxReduction43Amount || 0, minorUnit);
          }
        } else {
          // Tính lại các giá trị với cấu hình đã clone
          const chargesAmount = this.calculateChargeAmountInternal(this.editItem);
          const taxAmount = this.calculateTaxAmountInternal(this.editItem, chargesAmount);
          const totalAmount = this.calculateTotalAmountInternal(
            this.editItem, 
            chargesAmount, 
            taxAmount
          );

          // Đảm bảo cập nhật các giá trị đã tính toán mới nhất
          this.editItem.ChargesAmount = chargesAmount;
          this.editItem.TaxAmount = taxAmount;
          this.editItem.TotalAmount = totalAmount;
          
          if (this.clonedConfig.InvoiceType === 'BH_INVOICE') {
            this.editItem.TaxReduction43Amount = this.calculateTaxReductionInternal(this.editItem, taxAmount);
          }
        }
        
        // Gán lại loại hóa đơn từ cấu hình đã clone
        this.editItem.InvoiceType = this.clonedConfig.InvoiceType;
        
        // Validate dựa trên loại hóa đơn
        if (this.clonedConfig.InvoiceType === 'VAT_INVOICE' || this.clonedConfig.InvoiceType === 'BH_INVOICE') {
          // Nếu là hóa đơn VAT hoặc BH, kiểm tra giá trị Tax nếu hiển thị cột tax
          if (this.showTaxPercentColumnCloned && this.editItem.Tax > 100) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('validation_error'),
                text: 'Phần trăm thuế không được vượt quá 100%',
                variant: 'danger',
              },
            });
            return;
          }
        } else if (this.clonedConfig.InvoiceType === 'SALES_INVOICE') {
          // Nếu là hóa đơn bán hàng không thuế, đảm bảo Tax = 0
          this.editItem.Tax = 0;
          this.editItem.TaxAmount = 0;
        }
        
        // Kiểm tra Charges
        if (this.showChargePercentColumnCloned && this.editItem.Charges > 100) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('validation_error'),
              text: 'Phần trăm phí không được vượt quá 100%',
              variant: 'danger',
            },
          });
          return;
        }
        
        // Kiểm tra Quantity
        if (this.editItem.Quantity <= 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('validation_error'),
              text: 'Số lượng phải lớn hơn 0',
              variant: 'danger',
            },
          });
          return;
        }
      } else {
        // Khi auto calculate tắt, vẫn đảm bảo số lượng > 0
        if (this.editItem.Quantity <= 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('validation_error'),
              text: 'Số lượng phải lớn hơn 0',
              variant: 'danger',
            },
          });
          return;
        }
        
        // Gán lại loại hóa đơn từ cấu hình đã clone
        this.editItem.InvoiceType = this.clonedConfig.InvoiceType;
      }
      
      // Reset flag để lần sau mở popup không bị ảnh hưởng
      this.isEditingTotal = false;
      
      // Tạo bản sao của dữ liệu để tránh tham chiếu lẫn nhau
      const finalItem = JSON.parse(JSON.stringify(this.editItem));
      
      // Emit sự kiện với dữ liệu đã chỉnh sửa
      this.$emit('save', finalItem);
      
      // Đóng modal
      this.isModalOpen = false;
      this.$nextTick(() => {
        if (this.$refs.modalRef) {
          this.$refs.modalRef.hide();
        } else {
          this.$bvModal.hide('item-edit-popup');
        }
      });
    },
    resetForm() {
      if (!this.isModalOpen) {
        this.editItem = {};
        this.originalItem = {};
        this.clonedConfig = {};
        this.isEditingTotal = false;
      }
    }
  }
};
</script>

<style scoped>
.item-edit-container {
  width: 100%;
  overflow-x: auto; /* Cho phép cuộn ngang nếu quá dài */
}

.preview-note {
  font-style: italic;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #17a2b8;
}

.auto-calc-disabled {
  border-left: 3px solid #ffc107 !important;
  background-color: #fff8e1 !important;
}

.config-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.config-item {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #e9ecef;
  font-size: 0.85rem;
}

.vat-invoice {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.bh-invoice {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.sales-invoice {
  background-color: #fff3e0;
  color: #e65100;
}

.text-warning {
  color: #f57c00 !important;
}

.text-success {
  color: #2e7d32 !important;
}

.item-edit-header, .item-edit-body {
  display: flex;
  width: 100%;
  min-width: 1000px; /* Đảm bảo có đủ không gian */
}

.edit-column {
  padding: 0 4px;
  flex: 1;
  min-width: 80px; /* Đảm bảo kích thước tối thiểu */
}

/* Tùy chỉnh độ rộng cột */
.edit-column:nth-child(1) {
  flex: 3;  /* Tên sản phẩm rộng hơn */
  min-width: 200px;
}

.edit-column:nth-child(2) {
  flex: 1;  /* Đơn vị */
  min-width: 80px;
}

.edit-column:nth-child(3) {
  flex: 0.8;  /* Số lượng */
  min-width: 60px;
}

.edit-column:nth-child(4) {
  flex: 1.2;  /* Đơn giá */
  min-width: 100px;
}

.edit-column:nth-child(5), 
.edit-column:nth-child(6), 
.edit-column:nth-child(7) {
  flex: 1.2;  /* Discount, Charge, Tax */
  min-width: 80px;
}

.edit-column:nth-child(8),
.edit-column:nth-child(9),
.edit-column:nth-child(10),
.edit-column:nth-child(11) {
  flex: 1.2;  /* Calculated fields */
  min-width: 100px;
  background-color: #f8f9fa; /* Màu nền nhẹ để phân biệt */
}

.calculated-field {
  background-color: #f8f9fa !important;
  border-color: #e9ecef !important;
  color: #6c757d !important; /* Màu chữ nhẹ hơn để thể hiện đây là giá trị chỉ để hiển thị */
  font-style: italic !important; /* In nghiêng để phân biệt */
}

.manual-input {
  background-color: #fff3cd !important;
  border-color: #ffeeba !important;
  color: #856404 !important;
}

.auto-calc-disabled {
  background-color: #fff3cd !important;
  border-color: #ffeeba !important;
  border-left: 3px solid #856404 !important;
}

.edit-column:nth-child(10) .form-control {
  background-color: #e7f3ff !important;
  border-color: #007bff !important;
  color: #000 !important;
  font-weight: bold !important;
}

.editable-total {
  background-color: #e7f3ff !important;
  border-color: #007bff !important;
  color: #000 !important;
  font-weight: bold !important;
  position: relative;
}

.editable-total:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}

.text-right {
  text-align: right;
}

/* Thêm các styles mới */
.invoice-type-badge {
  position: absolute;
  top: 10px;
  right: 15px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
}

.vat-invoice {
  background-color: #d4edda;
  color: #155724;
}

.bh-invoice {
  background-color: #cce5ff;
  color: #004085;
}

.sales-invoice {
  background-color: #fff3cd;
  color: #856404;
}

.editable-total.calculated {
  background-color: #9ee3ff !important;
  transition: background-color 0.3s ease;
}

.item-edit-body .form-control:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.config-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.config-item {
  background-color: #f1f1f1;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Vue Cleave styling */
.edit-column .vue-cleave {
  width: 100%;
}

.edit-column .vue-cleave input {
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #6e6b7b;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.edit-column .vue-cleave input:focus {
  color: #6e6b7b;
  background-color: #fff;
  border-color: #7367f0;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.edit-column .vue-cleave input.calculated-field {
  background-color: #f8f9fa !important;
  border-color: #e9ecef !important;
  color: #6c757d !important;
  font-style: italic !important;
}

.edit-column .vue-cleave input.manual-input {
  background-color: #fff3cd !important;
  border-color: #ffeeba !important;
  color: #856404 !important;
}

.edit-column .vue-cleave input.editable-total {
  background-color: #e7f3ff !important;
  border-color: #007bff !important;
  color: #000 !important;
  font-weight: bold !important;
}

.edit-column .vue-cleave input.editable-total:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}
</style>